import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useRef } from 'react'
import { products, fontSans, hexToRgba } from './theme'

const CONTACT_EMAIL = 'hello@synkrasis.ai'

export function CTASection() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })

  return (
    <section
      id="demo"
      style={{
        padding: '7rem 2rem',
        background: '#0A0F1F',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(74, 144, 226, 0.4), rgba(157, 78, 221, 0.4), transparent)',
        }}
      />

      {/* Background glow blobs */}
      <div
        style={{
          position: 'absolute',
          top: '-100px',
          left: '-100px',
          width: '500px',
          height: '500px',
          background:
            'radial-gradient(circle, rgba(74, 144, 226, 0.07) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: '-100px',
          right: '-100px',
          width: '400px',
          height: '400px',
          background:
            'radial-gradient(circle, rgba(157, 78, 221, 0.08) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none',
        }}
      />

      <div
        style={{ maxWidth: '760px', margin: '0 auto', position: 'relative' }}
      >
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          style={{ textAlign: 'center', marginBottom: '3rem' }}
        >
          <span
            style={{
              fontFamily: fontSans,
              fontSize: '0.75rem',
              fontWeight: 600,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: '#4A90E2',
              display: 'block',
              marginBottom: '1rem',
            }}
          >
            Get Started
          </span>
          <h2
            style={{
              fontFamily: fontSans,
              fontSize: 'clamp(2rem, 4vw, 3.2rem)',
              fontWeight: 800,
              color: '#ffffff',
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              marginBottom: '1.25rem',
            }}
          >
            Ready to Move to{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Agentic Engineering?
            </span>
          </h2>
          <p
            style={{
              fontFamily: fontSans,
              fontSize: '1.05rem',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.7,
              maxWidth: '540px',
              margin: '0 auto',
            }}
          >
            Stop building pipelines. Start orchestrating intelligence. Tell us
            which pain to solve first — the platform, the ontology, or PII.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: '24px',
            padding: 'clamp(2rem, 5vw, 3.5rem)',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontFamily: fontSans,
              fontSize: '1.15rem',
              fontWeight: 700,
              color: 'rgba(255,255,255,0.92)',
              marginBottom: '0.5rem',
            }}
          >
            Book your strategy session
          </div>
          <p
            style={{
              fontFamily: fontSans,
              fontSize: '0.92rem',
              color: 'rgba(255,255,255,0.5)',
              lineHeight: 1.7,
              maxWidth: '420px',
              margin: '0 auto 2rem',
            }}
          >
            Email us and we&apos;ll set up a live walkthrough of the loop —
            platform, Ontext or Sentinel. No sales pressure, just a real
            conversation about your data challenges.
          </p>

          <a
            href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(
              'Demo request — SynKrasis',
            )}`}
            style={{
              display: 'inline-block',
              fontFamily: fontSans,
              fontSize: '1rem',
              fontWeight: 600,
              color: '#ffffff',
              textDecoration: 'none',
              padding: '1rem 2.4rem',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #4A90E2, #9D4EDD)',
              boxShadow: '0 0 30px rgba(74, 144, 226, 0.3)',
              transition: 'all 0.25s ease',
              letterSpacing: '0.01em',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)'
              e.currentTarget.style.boxShadow =
                '0 8px 40px rgba(74, 144, 226, 0.5)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)'
              e.currentTarget.style.boxShadow =
                '0 0 30px rgba(74, 144, 226, 0.3)'
            }}
          >
            Email {CONTACT_EMAIL}
          </a>

          <div
            style={{
              fontFamily: fontSans,
              fontSize: '0.8rem',
              color: 'rgba(255,255,255,0.35)',
              marginTop: '1.5rem',
            }}
          >
            We reply within 24 hours · USA · Dubai · India
          </div>

          {/* product interest strip */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.6rem',
              flexWrap: 'wrap',
              marginTop: '1.75rem',
              paddingTop: '1.5rem',
              borderTop: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {products.map((p) => (
              <span
                key={p.id}
                style={{
                  fontFamily: fontSans,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  color: p.accent,
                  padding: '6px 14px',
                  borderRadius: '100px',
                  background: hexToRgba(p.accent, 0.08),
                  border: `1px solid ${hexToRgba(p.accent, 0.3)}`,
                }}
              >
                {p.name}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
